import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import API_BASE_URL from '../config';
import './AdminPhotographers.css';

interface PhotographerInfo {
    _id: string;
    name: string;
    email: string;
    phone?: string;
}

const AdminPhotographers: React.FC = () => {
    const { accessToken } = useAuth();
    const [photographers, setPhotographers] = useState<PhotographerInfo[]>([]);
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [phone, setPhone] = useState('');
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(true);
    const [message, setMessage] = useState({ type: '', text: '' });

    const fetchPhotographers = async () => {
        try {
            const res = await fetch(`${API_BASE_URL}/api/admin/photographers`, {
                headers: { 'Authorization': `Bearer ${accessToken}` }
            });
            const data = await res.json();
            if (res.ok) {
                setPhotographers(data.photographers);
            }
        } catch (err) {
            console.error('Failed to fetch photographers');
        } finally {
            setFetching(false);
        }
    };

    useEffect(() => {
        if (accessToken) {
            fetchPhotographers();
        }
    }, [accessToken]);

    const handleCreate = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setMessage({ type: '', text: '' });

        try {
            const res = await fetch(`${API_BASE_URL}/api/admin/create-photographer`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${accessToken}`
                },
                body: JSON.stringify({ name, email, password, phone })
            });

            const data = await res.json();
            if (res.ok) {
                setMessage({ type: 'success', text: 'Photographer account created successfully!' });
                setName('');
                setEmail('');
                setPassword('');
                setPhone('');
                fetchPhotographers(); // Refresh list
            } else {
                setMessage({ type: 'error', text: data.message || 'Failed to create photographer' });
            }
        } catch (err) {
            setMessage({ type: 'error', text: 'Network error occurred' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="admin-photographers-container">

            <div className="admin-photographers-content">
                <header className="header-section">
                    <h1>Manage Photographers</h1>
                    <p>Onboard and manage professional photographers for your wedding gallery.</p>
                </header>

                <div className="management-grid">
                    {/* Create Form */}
                    <div className="premium-card">
                        <h2 className="card-title"><span>🆕</span> Add New Expert</h2>
                        {message.text && (
                            <div className={`message ${message.type}`}>
                                {message.text}
                            </div>
                        )}
                        <form onSubmit={handleCreate}>
                            <div className="form-group">
                                <label>Full Name</label>
                                <input
                                    type="text"
                                    value={name}
                                    onChange={e => setName(e.target.value)}
                                    required
                                    placeholder="e.g. Rahul Photography"
                                />
                            </div>
                            <div className="form-group">
                                <label>Email Address</label>
                                <input
                                    type="email"
                                    value={email}
                                    onChange={e => setEmail(e.target.value)}
                                    required
                                    placeholder="expert@shadigram.com"
                                />
                            </div>
                            <div className="form-group">
                                <label>Temporary Password</label>
                                <input
                                    type="password"
                                    value={password}
                                    onChange={e => setPassword(e.target.value)}
                                    required
                                    placeholder="Min. 6 characters"
                                />
                            </div>
                            <div className="form-group">
                                <label>Phone (Optional)</label>
                                <input
                                    type="text"
                                    value={phone}
                                    onChange={e => setPhone(e.target.value)}
                                    placeholder="+91 XXXXX XXXXX"
                                />
                            </div>
                            <button type="submit" className="add-btn" disabled={loading}>
                                {loading ? '✨ Creating Account...' : 'Add to Wedding Team'}
                            </button>
                        </form>
                    </div>

                    {/* Team List */}
                    <div className="premium-card">
                        <h2 className="card-title"><span>📸</span> Your Photography Team</h2>
                        {fetching ? (
                            <div className="empty-state">Loading team members...</div>
                        ) : photographers.length === 0 ? (
                            <div className="empty-state">
                                <p>No photographers added yet.</p>
                                <small>Add your first expert using the form.</small>
                            </div>
                        ) : (
                            <div className="photographers-list">
                                {photographers.map(p => (
                                    <div key={p._id} className="photographer-item">
                                        <div className="photographer-avatar">
                                            {p.name.charAt(0).toUpperCase()}
                                        </div>
                                        <div className="photographer-info">
                                            <h4>{p.name}</h4>
                                            <p>{p.email}</p>
                                            {p.phone && <p>{p.phone}</p>}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminPhotographers;
